import { isShopifyEmbedded } from "@shopify/app-bridge-utils"
import { Button, Icon, LegacyCard, OptionList, Page, Popover, Text } from "@shopify/polaris"
import {
  CalendarIcon,
  CartUpIcon,
  CodeIcon,
  InfoIcon,
  LanguageIcon,
  LinkIcon,
  MarketsIcon,
  MenuHorizontalIcon,
  OrdersStatusIcon,
  StoreIcon,
  ViewIcon,
} from "@shopify/polaris-icons"
import classNames from "classnames"
import { Observer } from "mobx-react"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Outlet, useLocation, useNavigate } from "react-router-dom"

import { HotBadge } from "@/component"
import stores from "@/stores"
import { adminScopedTrackingPageTabs } from "@/utils/customerAuthGate"
import { openLink } from "@/utils/redirectShopify"

import SkeletonTrackingPage from "../SkeletonTrackingPage/SkeletonTrackingPage"
import styles from "./TabPage.module.scss"

export default function TabPage(props: any) {
  const { t } = useTranslation("tracking_page")
  const { userInfoSore, commonStore } = stores
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [selected, setSelected] = useState<string[]>(["appearance"])

  /**
   * 子页 id → 跳转路径
   * 超管后台白名单模式下 appearance 对应的是 /tracking-page 根路由（见 utils/customerAuthGate）
   */
  const resolveTabPath = useCallback((id: string) => (
    userInfoSore.isAdminScoped
      ? adminScopedTrackingPageTabs().get(id) ?? `/tracking-page/${id}`
      : `/tracking-page/${id}`
  ), [userInfoSore])

  // 处理 tab 切换，检查是否有未保存的更改
  const handleTabChange = useCallback((newSelected: string[]) => {
    // 如果有未保存的更改，不更新状态也不导航
    // NavigationBlocker 会通过 usePrompt 拦截导航并显示确认对话框
    if (commonStore.contextualSaveBar.show) {
      // 尝试导航，NavigationBlocker 会拦截并显示确认对话框
      navigate(resolveTabPath(newSelected[0]))
      // 不更新 selected 状态，保持当前选中状态
      return
    }
    // 没有未保存更改，正常更新状态和导航
    setSelected(newSelected)
    navigate(resolveTabPath(newSelected[0]))
  }, [commonStore.contextualSaveBar.show, navigate, resolveTabPath])


  const [popoverActive, setPopoverActive] = useState(false)

  const togglePopoverActive = useCallback(
    () => setPopoverActive((popoverActive) => !popoverActive),
    [],
  )

  useEffect(() => {
    if (pathname.startsWith("/tracking-page/")) {
      const urlName = pathname.substring(15)  // 获取/tracking-page/后面的

      setSelected([urlName])
    }
    if (pathname === "/tracking-page") {
      setSelected(["appearance"])
    }
  }, [pathname])

  if (pathname === "/tracking-page/customizeBackdrop" || pathname === "/tracking-page/customizeShowcase") {
    return <Outlet />
  }

  const tabListText = [
    {
      icon: StoreIcon, id: "appearance", name: t("TabPage.Appearance"),
    },
    {
      icon: LanguageIcon, id: "languages", name: t("TabPage.Languages"),
    },
    {
      icon: OrdersStatusIcon,
      id: "customShipmentStatus",
      name: t("TabPage.CustomShipmentStatus"),
    },
    {
      icon: CalendarIcon,
      id: "estimatedDeliveryTime",
      name: t("TabPage.EstimatedDeliveryTime"),
    },
    {
      icon: CartUpIcon,
      id: "productRecommendation",
      name: t("TabPage.ProductRecommendation"),
    },
    // {
    //   id: 'additionalText', icon: TextIcon, name: t('TabPage.AdditionalText'),
    // },
    {
      icon: MarketsIcon,
      id: "seoOptimization",
      name: t("TabPage.SEOOptimization"),
    },
    {
      icon: LinkIcon,
      id: "trackingPageUrl",
      name: t("TabPage.TrackingPageUrl"),
    },
    {
      icon: CodeIcon, id: "CSS&HTML", name: t("TabPage.CSS&HTML"),
    },
  ]
  const hotList = ["productRecommendation", "estimatedDeliveryTime", "customShipmentStatus"]

  /**
   * 超管后台代客进入时，左侧只保留白名单里配置了 trackingPageTabId 的子页（见 utils/customerAuthGate）。
   * 写成函数是为了在 <Observer> 渲染期间读取，授权状态变化能即时刷新菜单。
   */
  const getVisibleTabList = () => {
    if (!userInfoSore.isAdminScoped) return tabListText

    const allowed = adminScopedTrackingPageTabs()

    return tabListText.filter(item => allowed.has(item.id))
  }

  return <Observer>{() => (
    <>
      <Page
        maxWidth={"1096px"}
        // @ts-ignore
        title={t("tracking_page", { ns: "menu" })}
        // @ts-ignore
        subtitle={
          <div className={" flex gap-1 items-center"}>
            <div
              className={"hover:underline cursor-pointer break-words  whitespace-nowrap"}
              onClick={() => openLink(`https://${userInfoSore.data.last_track_url}`)}
            >
              <Text variant={"bodySm"} as={"p"}>https://{userInfoSore.data.last_track_url}</Text>
            </div>
            <a href="https://docs.parcelpanel.com/shopify/getting-started/add-the-tracking-page/" target={"_blank"} rel="noreferrer">
              <Icon tone={"subdued"} source={InfoIcon} />
            </a>
          </div>
        }
        secondaryActions={
          <div className={classNames(styles.tabPageTitle, styles["tabPageTitle-mobile"])}>
            <Popover
              active={popoverActive}
              activator={
                <Button onClick={togglePopoverActive} variant={"tertiary"} icon={MenuHorizontalIcon} />
              }
              autofocusTarget="first-node"
              onClose={togglePopoverActive}
            >
              <div className={classNames("p-[var(--p-space-150)]", styles.mobileOptionList)}>{<OptionList
                onChange={(e) => {
                  togglePopoverActive()
                  handleTabChange(e)
                }}
                options={getVisibleTabList().map(item => {
                  return {
                    label: <div key={item.id} className={"flex items-center gap-[var(--p-space-200)]"}>
                      <Icon source={item.icon} />
                      {item.name}
                    </div>,
                    value: item.id,
                  }
                })}
                selected={selected}
              />}</div>
            </Popover>
          </div>
        }
        primaryAction={
          <Button url={`https://${userInfoSore.data.last_track_url}?nums=1234&preview=parcelpanel`} external
            variant="primary"
            icon={ViewIcon}
          >
            {t("preview")}
          </Button>
        }
      />

      <div style={{ display: "flex", justifyContent: "center" }}>
        <div className={styles.isTabPage}>
          <div className={styles.tabPage} style={{ top: isShopifyEmbedded() ? 20 : 70 }}>
            <LegacyCard>
              <OptionList
                onChange={handleTabChange}
                options={getVisibleTabList().map(item => {
                  return {
                    label: <div key={item.id} className={"flex items-center gap-[var(--p-space-100)]"}>
                      <Icon source={item.icon} />
                      <div className={"ml-1 flex items-center"}>
                        <div>{item.name}</div>
                        {hotList.includes(item.id) && <HotBadge className={["inline-block", "ml-1", "min-w-[37px]"]}>Hot</HotBadge>}
                      </div>
                    </div>,
                    value: item.id,
                  }
                })}
                selected={selected}
              />
            </LegacyCard>
          </div>
        </div>
        <div className={styles.tabPageContent}>
          <div className={styles.tabPageContentList}>
            <div>
              {
                props.loading
                  ? <SkeletonTrackingPage />
                  : <Outlet />
              }
            </div>
          </div>
        </div>
      </div>
    </>
  )}</Observer>
}
